const smallScreen = matchMedia('(max-width: 1023px)');
const landingCueScreen = matchMedia('(max-width: 639px)');
const giftGuideCueScreen = matchMedia('(max-width: 719px)');
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const entries = new Map();
const dirtyEntries = new Set();
const dismissedTracks = new Set();
const cuedTracks = new Set();
const horizontalGestureDistance = 12;
const interactionKeys = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End']);
const cueTrackSelector = '.gift-guide__track, .landing-product-track';

const overflowed = (track) => track.scrollWidth > track.clientWidth + 2;

const renderedTrack = (track) => (
  track.getClientRects().length > 0
  && !track.closest('[hidden]')
  && getComputedStyle(track).visibility !== 'hidden'
);

const trackKey = (track) => track.id ? `id:${track.id}` : track;

const cueScreenMatches = (track) => (
  track.matches('.gift-guide__track') ? giftGuideCueScreen.matches : landingCueScreen.matches
);

const visibleTrackChildren = (track) => Array.from(track.children).filter(
  (child) => !child.hidden && child.getClientRects().length > 0,
);

const cancelCue = (entry, consume = entry.cueState === 'playing') => {
  if (entry.cueState === 'finished') return;

  entry.cueRun += 1;
  for (const animation of entry.cueAnimations) animation.cancel();
  entry.cueAnimations = [];

  if (consume) {
    cuedTracks.add(entry.cueKey);
    entry.cueState = 'finished';
    entry.hint.classList.remove('is-cue-pending');
  }
};

const playCue = (entry, cards) => {
  if (entry.cueState !== 'pending') return;
  if (typeof cards[0]?.animate !== 'function') {
    cancelCue(entry, true);
    return;
  }

  entry.cueState = 'playing';
  const cueRun = ++entry.cueRun;
  entry.cueAnimations = cards.slice(0, 2).map((card) => card.animate(
    [
      // Reveal over 400ms, hold for 1s, then settle gently over 550ms.
      { transform: 'translateX(0)', easing: 'cubic-bezier(0.42, 0, 0.58, 1)' },
      { transform: 'translateX(-16px)', offset: 400 / 1950 },
      { transform: 'translateX(-16px)', offset: 1400 / 1950, easing: 'cubic-bezier(0.42, 0, 0.58, 1)' },
      { transform: 'translateX(2px)', offset: 1800 / 1950, easing: 'ease-in-out' },
      { transform: 'translateX(0)' },
    ],
    { duration: 1950, easing: 'linear' },
  ));

  Promise.allSettled(entry.cueAnimations.map((animation) => animation.finished)).then(() => {
    if (entry.cueState !== 'playing' || entry.cueRun !== cueRun) return;
    entry.cueAnimations = [];
    cuedTracks.add(entry.cueKey);
    entry.cueState = 'finished';
    entry.hint.classList.remove('is-cue-pending');
  });
};

const cleanupEntry = (hint, entry) => {
  cancelCue(entry);
  entry.abort.abort();
  entry.resize?.disconnect();
  entry.mutation.disconnect();
  entry.visibility?.disconnect();
  entry.proximity?.disconnect();
  dirtyEntries.delete(entry);
  entries.delete(hint);
};

let updateFrame = 0;

const renderUpdates = () => {
  updateFrame = 0;
  const measurements = [];
  const pending = Array.from(dirtyEntries);
  dirtyEntries.clear();
  for (const entry of pending) {
    const { hint } = entry;
    if (!hint.isConnected || !entry.track.isConnected) {
      cleanupEntry(hint, entry);
      continue;
    }

    // Keep initial hint layout and active cue validation independent of proximity.
    const measureTrack = entry.initialMeasurement || entry.nearby || entry.cueState === 'playing';
    const overflow = measureTrack ? overflowed(entry.track) : entry.overflow;
    const rendered = measureTrack && renderedTrack(entry.track);
    const scrollLeft = measureTrack && entry.hasSwipeCue && entry.cueState !== 'finished'
      ? entry.track.scrollLeft : 0;
    const measureCards = entry.hasSwipeCue
      && entry.cueState !== 'finished'
      && (entry.visible || entry.cueState === 'playing')
      && rendered
      && overflow
      && !entry.dismissed
      && !document.hidden
      && !reducedMotion.matches
      && cueScreenMatches(entry.track)
      && (entry.cueState === 'playing' || scrollLeft <= 2);
    const cards = measureCards ? visibleTrackChildren(entry.track) : [];
    entry.overflow = overflow;
    entry.initialMeasurement = false;
    const singleCard = cards.length > 1 && cards[0].getBoundingClientRect().width > entry.track.clientWidth * 0.75;
    measurements.push({ hint, entry, overflow, rendered, cards, singleCard, scrollLeft });
  }

  // Read every rail before changing hint layout. Observers share one frame.
  for (const { hint, entry, overflow, rendered, cards, singleCard, scrollLeft } of measurements) {
    hint.hidden = !overflow;
    hint.classList.toggle('is-dismissed', entry.dismissed);
    hint.setAttribute('aria-hidden', String(entry.dismissed));
    hint.classList.toggle(
      'is-paused',
      document.hidden || !entry.visible || !rendered || !smallScreen.matches || !overflow || entry.dismissed,
    );

    if (!entry.hasSwipeCue || entry.cueState === 'finished') continue;

    if (reducedMotion.matches) {
      cancelCue(entry, true);
      continue;
    }
    if (document.hidden) {
      cancelCue(entry);
      continue;
    }

    const cueEligible = (
      !entry.dismissed
      && entry.visible
      && rendered
      && overflow
      && singleCard
      && scrollLeft <= 2
      && cueScreenMatches(entry.track)
    );
    hint.classList.toggle(
      'is-cue-pending',
      cueEligible && (entry.cueState === 'pending' || entry.cueState === 'playing'),
    );

    if (entry.cueState === 'playing' && (!rendered || !overflow || !singleCard || !cueScreenMatches(entry.track) || entry.dismissed)) {
      cancelCue(entry, true);
    } else if (cueEligible) {
      playCue(entry, cards);
    }
  }
};

const scheduleUpdate = (entry) => {
  dirtyEntries.add(entry);
  if (!updateFrame) updateFrame = requestAnimationFrame(renderUpdates);
};

const update = () => {
  for (const entry of entries.values()) scheduleUpdate(entry);
};

const dismiss = (entry) => {
  if (entry.dismissed) return;
  entry.dismissed = true;
  dismissedTracks.add(entry.track.id || entry.track);
  scheduleUpdate(entry);
};

const resolveTrack = (hint) => {
  if (hint.dataset.swipeTarget) return document.getElementById(hint.dataset.swipeTarget);

  return hint
    .closest('[data-product-carousel], [data-for-you-rail], [data-quick-buy-rail], [data-quick-buy-panel]')
    ?.querySelector('[data-product-track], [data-for-you-track], [data-quick-buy-track], [data-quick-buy-rail]');
};

const resolveInteractionScope = (hint, track) =>
  hint.closest('[data-product-carousel], [data-for-you-rail], [data-quick-buy-rail], [data-quick-buy-panel]')
  || track.closest('[data-product-carousel], [data-for-you-rail], [data-quick-buy-rail], [data-quick-buy-panel]')
  || track.parentElement?.closest('[data-quick-buy-panel]')
  || track.parentElement;

const observeEntryMutations = (entry) => {
  entry.mutation.disconnect();
  entry.mutation.observe(entry.track, { childList: true, subtree: true });
  if (entry.scope && entry.scope !== entry.track) {
    entry.mutation.observe(entry.scope, {
      attributeFilter: ['aria-hidden', 'class', 'hidden'],
      attributes: true,
    });
  }
  // Revealing a deferred rail must refresh it even above its interaction scope.
  for (let ancestor = entry.track.parentElement; ancestor; ancestor = ancestor.parentElement) {
    entry.mutation.observe(ancestor, {
      attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
      attributes: true,
    });
  }
};

const initializeHint = (hint) => {
  if (entries.has(hint)) {
    scheduleUpdate(entries.get(hint));
    return;
  }

  const track = resolveTrack(hint);
  if (!track) return;

  const scope = resolveInteractionScope(hint, track);
  const abort = new AbortController();
  const cueKey = trackKey(track);
  const hasSwipeCue = track.matches(cueTrackSelector);
  const entry = {
    abort,
    cueAnimations: [],
    cueKey,
    cueRun: 0,
    cueState: cuedTracks.has(cueKey) ? 'finished' : 'pending',
    dismissed: dismissedTracks.has(track.id || track),
    hasSwipeCue,
    hint,
    initialMeasurement: true,
    mutation: new MutationObserver(() => scheduleUpdate(entry)),
    nearby: typeof IntersectionObserver !== 'function',
    overflow: false,
    pointer: null,
    proximity: null,
    resize: typeof ResizeObserver === 'function' ? new ResizeObserver(() => scheduleUpdate(entry)) : null,
    scrollIntentUntil: 0,
    scrollStart: 0,
    scope,
    track,
    visibility: null,
    visible: false,
  };
  const { signal } = abort;

  if (hasSwipeCue) hint.classList.add('has-swipe-cue');
  entries.set(hint, entry);
  entry.resize?.observe(track);
  observeEntryMutations(entry);

  if (typeof IntersectionObserver === 'function') {
    entry.visibility = new IntersectionObserver(
      ([observed]) => {
        entry.visible = Boolean(observed?.isIntersecting && observed.intersectionRatio >= 0.5);
        scheduleUpdate(entry);
      },
      { threshold: 0.5 },
    );
    entry.visibility.observe(track);
    entry.proximity = new IntersectionObserver(
      ([observed]) => {
        entry.nearby = Boolean(observed?.isIntersecting);
        scheduleUpdate(entry);
      },
      { rootMargin: '200px', threshold: 0 },
    );
    entry.proximity.observe(track);
  } else {
    entry.visible = true;
    if (hasSwipeCue) cancelCue(entry, true);
  }

  scheduleUpdate(entry);

  const startGesture = (clientX, clientY) => {
    entry.pointer = { clientX, clientY };
  };
  const continueGesture = (clientX, clientY) => {
    if (!entry.pointer) return;
    const deltaX = clientX - entry.pointer.clientX;
    const deltaY = clientY - entry.pointer.clientY;
    if (Math.abs(deltaX) >= horizontalGestureDistance && Math.abs(deltaX) > Math.abs(deltaY)) {
      cancelCue(entry, true);
      dismiss(entry);
      entry.pointer = null;
    }
  };
  const finishGesture = () => {
    entry.pointer = null;
  };

  track.addEventListener('pointerdown', (event) => {
    if (!event.isTrusted || !event.isPrimary || event.button !== 0) return;
    if (event.target === track) {
      entry.scrollStart = track.scrollLeft;
      entry.scrollIntentUntil = performance.now() + 1200;
    }
    startGesture(event.clientX, event.clientY);
  }, { passive: true, signal });
  track.addEventListener('pointermove', (event) => {
    if (!event.isTrusted || !event.isPrimary) return;
    continueGesture(event.clientX, event.clientY);
  }, { passive: true, signal });
  track.addEventListener('pointerup', finishGesture, { passive: true, signal });
  track.addEventListener('pointercancel', finishGesture, { passive: true, signal });

  track.addEventListener('touchstart', (event) => {
    if (!event.isTrusted || !event.touches[0]) return;
    startGesture(event.touches[0].clientX, event.touches[0].clientY);
  }, { passive: true, signal });
  track.addEventListener('touchmove', (event) => {
    if (!event.isTrusted || !event.touches[0]) return;
    continueGesture(event.touches[0].clientX, event.touches[0].clientY);
  }, { passive: true, signal });
  track.addEventListener('touchend', finishGesture, { passive: true, signal });
  track.addEventListener('touchcancel', finishGesture, { passive: true, signal });

  track.addEventListener('wheel', (event) => {
    if (!event.isTrusted) return;
    if (!overflowed(track)) return;
    const horizontalDelta = Math.abs(event.deltaX);
    const shiftedDelta = event.shiftKey ? Math.abs(event.deltaY) : 0;
    if (Math.max(horizontalDelta, shiftedDelta) >= horizontalGestureDistance
      && (event.shiftKey || horizontalDelta > Math.abs(event.deltaY))) {
      cancelCue(entry, true);
      dismiss(entry);
    }
  }, { passive: true, signal });
  track.addEventListener('scroll', () => {
    if (performance.now() > entry.scrollIntentUntil) return;
    if (Math.abs(track.scrollLeft - entry.scrollStart) > 2) {
      cancelCue(entry, true);
      dismiss(entry);
    }
  }, { passive: true, signal });

  scope?.addEventListener('click', (event) => {
    if (!event.isTrusted || hint.contains(event.target)
      || !event.target.closest('button, a, input, select, [role="button"]')) return;
    cancelCue(entry, true);
    dismiss(entry);
  }, { signal });
  scope?.addEventListener('keydown', (event) => {
    if (!event.isTrusted) return;
    if (!interactionKeys.has(event.key)) return;
    cancelCue(entry, true);
    dismiss(entry);
  }, { signal });
};

const initialize = (scope = document) => {
  if (scope instanceof Element && scope.matches('[data-product-swipe-hint]')) initializeHint(scope);
  scope.querySelectorAll?.('[data-product-swipe-hint]').forEach(initializeHint);
};

initialize();

const additions = new MutationObserver((records) => {
  const removedNodes = records.flatMap((record) => Array.from(record.removedNodes));
  if (removedNodes.length) {
    for (const [hint, entry] of Array.from(entries)) {
      if (!hint.isConnected || !entry.track.isConnected) {
        cleanupEntry(hint, entry);
      } else if (removedNodes.some((node) => node.contains(entry.track) || node.contains(hint))) {
        observeEntryMutations(entry);
        scheduleUpdate(entry);
      }
    }
  }
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('[data-product-swipe-hint]')) initializeHint(node);
      node.querySelectorAll('[data-product-swipe-hint]').forEach(initializeHint);
    }
  }
});
additions.observe(document.body, { childList: true, subtree: true });

smallScreen.addEventListener('change', update);
landingCueScreen.addEventListener('change', update);
giftGuideCueScreen.addEventListener('change', update);
reducedMotion.addEventListener('change', update);
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    for (const entry of entries.values()) cancelCue(entry);
  }
  update();
});
window.addEventListener('pagehide', () => {
  for (const entry of entries.values()) cancelCue(entry);
});
document.addEventListener('shopify:section:load', (event) => initialize(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  for (const [hint, entry] of entries) {
    if (event.target.contains(hint)) cleanupEntry(hint, entry);
  }
});
